import Config from 'config/settings';
import { Events } from 'core/events/Events';
import { DragBreakpoints } from 'interaction/DragBreakpoints';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { WorkConfig } from 'app/model/WorkConfig';
import WorkCarouselSizes from 'app/views/work/WorkCarouselSizes';
import { WorkDragDeps } from './work.types';
import { device } from 'utils/device';

/**
 * Drag side of the work section: the DragBreakpoints wiring plus the
 * velocity / rotation / frameProgress physics model. Owns the canonical
 * tileProgress value that the rest of the section reads each frame.
 */
export class WorkDrag {
	private _deps: WorkDragDeps;

	private _tileBreakpoints: DragBreakpoints;
	private _tileProgress: number;

	//Velocity / Rotation:
	private _frameProgress: number = 0;
	private _velocity: number = 0;
	private _moving: boolean = false;

	private _virtualWidth: number;

	private _allowWindDownUpdates: boolean = false;

	private _json: WorkConfig;

	constructor(deps: WorkDragDeps) {
		this._deps = deps;
	}

	//Accessors
	//-------------------------------------------------------------------------------------

	public get progress(): number {
		return this._tileProgress;
	}

	public get velocity(): number {
		return this._velocity;
	}

	public set allowWindDownUpdates(value: boolean) {
		this._allowWindDownUpdates = value;
	}

	/** Seed the progress value directly (used before the breakpoints exist) */
	public seedProgress(value: number): void {
		this._tileProgress = value;
	}

	//DragBreakpoints delegation
	//-------------------------------------------------------------------------------------

	public setActive(active: boolean): void {
		this._tileBreakpoints.setActive(active);
	}

	public setAllowDrag(allowDrag: boolean): void {
		this._tileBreakpoints.setAllowDrag(allowDrag);
	}

	public setIndex(index: number): void {
		this._tileBreakpoints.setIndex(index);
	}

	public removeVelocity(): void {
		this._tileBreakpoints.removeVelocity();
	}

	/** Reset the timeline right to start '0' progress */
	public resetProgress(): void {
		this._tileProgress = 0;
		this._frameProgress = 0;
		this._tileBreakpoints.setProgress(0);
	}

	/** Set the virtual width for the break points */
	public setVirtualWidthForTiles(tileCount: number): void {
		this._virtualWidth = tileCount * device.innerWidth * WorkCarouselSizes.widthMuliplier();
		this._tileBreakpoints.setVirtualWidth(this._virtualWidth);
	}

	//Creation
	//-------------------------------------------------------------------------------------

	public createBreakpoints(tileCount: number, json: WorkConfig, mouseController: MouseController): void {
		if (this._tileBreakpoints) {
			return;
		}

		this._json = json;

		//Create the 0% / 50% / 100% for SCREENS <> HOME <> SPACES layout
		this._tileBreakpoints = new DragBreakpoints(tileCount, true, 0, false);

		let allowProgressTo: number = 1.0 - 1.0 / tileCount;
		this._tileBreakpoints.setConstraints(0, allowProgressTo);
		this._tileBreakpoints.mouseController = mouseController;
		this._tileBreakpoints.setDragToMoveRatio(this._json.dragToMoveRatio);
		this._tileBreakpoints.easePower = this._json.dragEasePower;
		this._tileBreakpoints.releaseEasePower = this._json.easePower;
		this._tileBreakpoints.forceSnap = false;
		this._tileBreakpoints.snap = true;
		this._tileBreakpoints.setProgress(0);


		this._tileBreakpoints.onTick = (progressRatio: number) => {
			if (!this._deps.isActive() && !this._allowWindDownUpdates) {
				return;
			}

			this._frameProgress = progressRatio - this._tileProgress;

			this._velocity = this._frameProgress;
			this._tileProgress = progressRatio;

			this._deps.onFrame();
		};

		this._tileBreakpoints.onStartDrag = () => {
			if (!this._deps.isActive()) {
				return;
			}

			this._deps.onStartDrag();
		};

		this._tileBreakpoints.onStopDrag = () => {
			this._deps.onStopDrag();
		};

		this._tileBreakpoints.onSnap = (snapIndex: number) => {
			this._deps.onSnap(snapIndex);
		};

		this._tileBreakpoints.setActive(false);
	}

	//Animation helpers
	//-------------------------------------------------------------------------------------

	public slideOnFirstProject(): void {
		let slowdown = device.innerWidth < Config.mobileBreakpoint ? 2.3 : 1.5;
		this._tileBreakpoints.releaseEasePower = this._json.easePower * slowdown;

		setTimeout(() => {
			this._tileBreakpoints.releaseEasePower = this._json.easePower;
		}, 2000);

		this._tileBreakpoints.hardSetEase(this._json.easePower);
		this._tileBreakpoints.setIndex(1);
	}

	//Physics
	//-------------------------------------------------------------------------------------


	public processSnap(focusedIndex: number): void {
		if (!this._moving && Math.abs(this._velocity) > 0.0003) {
			this._moving = true;
			Events.dispatch('work_copy_hidden');
		} else if (this._moving && Math.abs(this._velocity) <= 0.0003 && !this._tileBreakpoints.isDragging) {
			Events.dispatch('work_slide_stopped', focusedIndex);
			this._moving = false;
		}
	}

}
